import React, { useEffect, useState } from 'react';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { formatApiErrorDetail } from '../../utils/apiErrorDetail';
import { checkOneSequence, getSequenceCheck, ignoreRecommendation } from '../../api/sequenceCheck';
import { Recommendation, sequenceStatus } from '../PlotTrack/PlotTrackSequenceCheck';
import {
  SEQ_BRIEF,
  SEQ_CHECK_ONE,
  SEQ_CHECK_ONE_ADVICE,
  SEQ_CHECK_ONE_BUSY,
  SEQ_CHECK_ONE_FAILED,
  SEQ_CHECK_ONE_READY,
  SEQUENCE_CHANGED_HINT,
  SEQUENCE_IGNORE,
  SEQUENCE_STATUS_NEEDS,
  SEQUENCE_STATUS_NO_TALK,
  SEQUENCE_STATUS_WARN,
} from '../../utils/writerCopy';

const WRITER = new Set(['user', 'human', 'writer']);
// Same bars as the server's pre-Generate check (generate_precheck.py).
export const MIN_REPLY_WORDS = 4;
export const BRIEF_WORDS = 40;

/** Writer words in replies of 4+ words: "ok" and "sounds good" are not talk. */
export function writerTalkWords(messages) {
  return (messages || []).reduce((total, message) => {
    const speaker = String(message?.speaker || message?.role || '').toLowerCase();
    if (!WRITER.has(speaker)) return total;
    const words = String(message?.content || message?.text || '').trim().split(/\s+/).filter(Boolean).length;
    return words >= MIN_REPLY_WORDS ? total + words : total;
  }, 0);
}

const sameName = (a, b) => String(a || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
  === String(b || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const STATUS_LABEL = {
  needs: SEQUENCE_STATUS_NEEDS,
  no_talk: SEQUENCE_STATUS_NO_TALK,
  warn: SEQUENCE_STATUS_WARN,
};

function errorCopy(err) {
  const raw = formatApiErrorDetail(err?.detail) || formatApiErrorDetail(err?.message) || '';
  const cleaned = raw.replace(/^VALIDATION:\s*[^-]+-\s*/, '');
  return cleaned && !/internal error|\[object Object\]/i.test(cleaned) ? cleaned : SEQ_CHECK_ONE_FAILED;
}

/**
 * Beside one sequence's Generate in Plot Focus: is there enough talk here?
 * Free: says so when the talk is a sentence or two. Paid, only on the
 * writer's click: Check this sequence (readiness + one recommendation).
 * Advice only. It never hides or blocks Generate, and any advice can be ignored.
 */
export default function SequenceTalkNote({ storyId, sequenceName, messages = [] }) {
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [result, setResult] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const count = (messages || []).length;
  const words = writerTalkWords(messages);
  const brief = words > 0 && words < BRIEF_WORDS;

  // The last check, if it covered this sequence and the talk hasn't moved (free).
  useEffect(() => {
    let live = true;
    setResult(null);
    setBaseline(null);
    setError(null);
    if (!storyId || !sequenceName) return undefined;
    getSequenceCheck(storyId).then((res) => {
      if (!live) return;
      const seq = (res?.check?.sequences || []).find((s) => sameName(s.name, sequenceName));
      if (seq && !seq.changed_since_check && !seq.dismissed) setResult(seq);
    }).catch(() => {});
    return () => { live = false; };
  }, [storyId, sequenceName]);

  // Messages can load after the check: the baseline is the first count seen with a result.
  useEffect(() => {
    if (result && baseline === null && count > 0) setBaseline(count);
  }, [result, baseline, count]);

  const stale = Boolean(result) && baseline !== null && count > baseline;

  const onCheck = async () => {
    if (!storyId || !sequenceName || busy) return;
    const ok = await askPaid('sequence_check_one');
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await checkOneSequence(storyId, sequenceName);
      setResult((res?.sequences || [])[0] || null);
      setBaseline(count > 0 ? count : null);
    } catch (err) {
      setError(errorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  const onIgnore = async () => {
    if (!result) return;
    try {
      await ignoreRecommendation(storyId, result.name, result.talk_fingerprint);
      setResult(null);
    } catch (err) {
      setError(errorCopy(err));
    }
  };

  const status = result ? sequenceStatus(result) : null;
  const advice = result && !stale && result.recommendation;

  return (
    <div className="seq-talk-note" role="region" aria-label="Chat for this sequence" aria-live="polite">
      {paidDialog}
      {brief && !result && <p className="seq-talk-note__line">{SEQ_BRIEF}</p>}
      {result && stale && <p className="seq-talk-note__line">{SEQUENCE_CHANGED_HINT}</p>}
      {result && !stale && status === 'decided' && (
        <p className="seq-talk-note__line" role="status">{SEQ_CHECK_ONE_READY}</p>
      )}
      {advice && (
        <>
          {STATUS_LABEL[status] && <p className="seq-talk-note__status">{STATUS_LABEL[status]}</p>}
          <Recommendation rec={result.recommendation} statusLabel={STATUS_LABEL[status]} />
        </>
      )}
      {error && <p className="seq-talk-note__line" role="alert">{error}</p>}
      {(brief || advice) && <p className="seq-talk-note__meta">{SEQ_CHECK_ONE_ADVICE}</p>}
      <div className="seq-talk-note__actions">
        <button
          type="button"
          className="seq-preview__text-btn"
          onClick={onCheck}
          disabled={busy || !storyId || !sequenceName}
        >
          {busy ? SEQ_CHECK_ONE_BUSY : SEQ_CHECK_ONE}
        </button>
        {advice && (
          <button type="button" className="seq-preview__text-btn" onClick={onIgnore}>
            {SEQUENCE_IGNORE}
          </button>
        )}
      </div>
    </div>
  );
}
